import {
  Card,
  Checkbox,
  Stack,
  Table,
  TableBody,
  TableCell,
  styled,
  useTheme,
} from '@mui/material';
import UserEditModal from 'components/modal/crm/UserEditModal';
import ReversePagination from 'components/ReversePagination';
import BlankCheckBoxIcon from 'icons/BlankCheckBoxIcon';
import CheckBoxIcon from 'icons/CheckBoxIcon';
import {
  forwardRef,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useFilters, usePagination, useRowSelect, useTable } from 'react-table';
import ScrollBar from 'simplebar-react';
import { customShadows } from 'theme/shadows';
import { StyledTableBodyRow } from '../dashboards/api-management/styledComponents';
import columnShape from './columnShape';

const SelectCheckBox = forwardRef(({ indeterminate, ...rest }, ref) => {
  const defaultRef = useRef();
  const resolvedRef = ref || defaultRef;
  useEffect(() => {
    if (resolvedRef) {
      resolvedRef.current.indeterminate = indeterminate;
    }
  }, [resolvedRef, indeterminate]);
  return (
    <Checkbox
      {...rest}
      ref={resolvedRef}
      disableRipple
      checkedIcon={<CheckBoxIcon fontSize="small" color="primary" />}
      icon={<BlankCheckBoxIcon fontSize="small" sx={{ color: '#CACBD2' }} />}
    />
  );
});

const BodyTableCell = styled(TableCell)(({ theme }) => ({
  fontSize: 15,
  fontWeight: 500,
  whiteSpace: 'nowrap',
  paddingTop: 16,
  paddingBottom: 16,
  paddingLeft: `1rem !important`,
  paddingRight: `1rem !important`,
  color: theme.palette.text.secondary,
  borderColor: 'secondary.300',
  '&:first-of-type': {
    paddingLeft: 24,
  },
  '&:last-of-type': {
    paddingRight: 24,
  },
}));

const DataTable = ({
  data,
  externalFilters,
  handleRowSelect,
  onFilterChange,
  onUserInfoClick,
}) => {
  const theme = useTheme();
  const [showModal, setShowModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);

  const handleEditClick = useCallback(userData => {
    setSelectedUser(userData);
    setShowModal(true);
  }, []);

  const handleBusinessInfoClick = useCallback(userData => {
    // Handle business info click - you can implement this later
    console.log('Business info clicked for:', userData);
  }, []);

  const tableData = useMemo(() => data, [data]);
  const columns = useMemo(
    () =>
      columnShape(handleEditClick, handleBusinessInfoClick, onUserInfoClick),
    [handleEditClick, handleBusinessInfoClick, onUserInfoClick]
  );

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    prepareRow,
    page,
    pageOptions,
    gotoPage,
    state,
    setAllFilters,
    selectedFlatRows,
  } = useTable(
    {
      columns,
      data: tableData,
      manualFilters: false,
    },
    useFilters,
    usePagination,
    useRowSelect,
    hooks => {
      hooks.visibleColumns.push(columns => [
        {
          id: 'selection',
          Header: ({ getToggleAllRowsSelectedProps }) => (
            <SelectCheckBox {...getToggleAllRowsSelectedProps()} />
          ),
          Cell: ({ row }) => {
            return <SelectCheckBox {...row.getToggleRowSelectedProps()} />;
          },
        },
        ...columns,
      ]);
    }
  );

  useEffect(() => {
    handleRowSelect(selectedFlatRows);
  }, [selectedFlatRows, handleRowSelect]);

  // Apply external filters
  useEffect(() => {
    if (externalFilters) {
      setAllFilters(externalFilters);
    }
  }, [externalFilters, setAllFilters]);

  useEffect(() => {
    if (onFilterChange) {
      onFilterChange(state.filters);
    }
  }, [onFilterChange, state.filters]);

  const handleChange = (_, currentPageNo) => {
    gotoPage(currentPageNo - 1);
  };

  const selectedRow = selectId => {
    const rowId = Object.keys(state.selectedRowIds);
    const findId = rowId.find(id => id === selectId);
    if (findId) return true;
    return false;
  };

  return (
    <>
      <Card
        sx={{
          padding: '24px',
          borderRadius: '18px',
          boxShadow: customShadows.card,
          backgroundColor: '#ffffff',
          border: 'none',
        }}
      >
        <ScrollBar>
          <Table {...getTableProps()}>
            <TableBody {...getTableBodyProps()}>
              {page.map(row => {
                prepareRow(row);
                return (
                  <StyledTableBodyRow
                    {...row.getRowProps()}
                    selected_row={selectedRow(row.id) ? 'select' : ''}
                  >
                    {row.cells.map(cell => {
                      // Skip rendering cells for hidden columns (columns with empty headers)
                      if (cell.column.Header === '') return null;

                      return (
                        <BodyTableCell
                          {...cell.getCellProps()}
                          sx={{
                            '&:first-child': {
                              paddingLeft: '16px',
                            },
                          }}
                        >
                          {cell.render('Cell')}
                        </BodyTableCell>
                      );
                    })}
                  </StyledTableBodyRow>
                );
              })}
            </TableBody>
          </Table>
        </ScrollBar>

        <Stack alignItems="center" marginY="1rem">
          <ReversePagination totalItems={pageOptions.length} />
        </Stack>
      </Card>

      <UserEditModal
        showModal={showModal}
        setShowModal={setShowModal}
        userData={selectedUser}
      />
    </>
  );
};

export default DataTable;
